
import { supabase } from '@/integrations/supabase/client';
import { config, currentEnvironment } from '@/config/environment';

export interface EnvironmentEmailConfig {
  environment: string;
  fromAddress: string;
  replyToAddress?: string;
  enabled: boolean;
  apiEndpoint: string;
  logToDatabase: boolean;
  batchNotificationsEnabled: boolean;
  batchSchedule: string;
}

export class EnvironmentEmailConfigManager {
  private static getDefaultConfig(): EnvironmentEmailConfig {
    const environmentConfigs = {
      development: {
        fromAddress: 'dev-notifications@nationcard.org',
        replyToAddress: 'dev-support@nationcard.org',
        batchSchedule: '0 */2 * * *' // Every 2 hours in dev
      },
      staging: {
        fromAddress: 'staging-notifications@nationcard.org', 
        replyToAddress: 'staging-support@nationcard.org',
        batchSchedule: '0 8 * * *' // Daily at 8 AM in staging
      },
      production: {
        fromAddress: 'notifications@nationcard.org',
        replyToAddress: 'support@nationcard.org',
        batchSchedule: '0 6 * * *' // Daily at 6 AM in production
      }
    };

    const envConfig = environmentConfigs[currentEnvironment as keyof typeof environmentConfigs];

    return {
      environment: currentEnvironment,
      ...envConfig,
      enabled: true,
      apiEndpoint: config.apiUrl,
      logToDatabase: true,
      batchNotificationsEnabled: true
    };
  }

  /**
   * Type guard to check if a value is a valid EnvironmentEmailConfig
   */
  private static isValidEmailConfig(value: any): value is EnvironmentEmailConfig {
    return (
      typeof value === 'object' &&
      value !== null &&
      typeof value.environment === 'string' &&
      typeof value.fromAddress === 'string' &&
      typeof value.enabled === 'boolean' &&
      typeof value.apiEndpoint === 'string' &&
      typeof value.logToDatabase === 'boolean' &&
      typeof value.batchNotificationsEnabled === 'boolean' &&
      typeof value.batchSchedule === 'string'
    );
  }

  /**
   * Get email configuration for current environment
   */
  static async getEmailConfig(): Promise<EnvironmentEmailConfig> {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', `email_config_${currentEnvironment}`)
        .maybeSingle();

      if (error || !data) {
        // Return default config if none exists
        return this.getDefaultConfig();
      }

      const configValue = data.value as unknown;
      if (this.isValidEmailConfig(configValue)) {
        return configValue;
      }

      // If stored config is invalid, return default config
      console.warn('Invalid email config found, using default:', configValue);
      return this.getDefaultConfig();
    } catch (error) {
      console.error('Error fetching email config:', error);
      return this.getDefaultConfig();
    }
  }

  /**
   * Save email configuration for current environment
   */
  static async saveEmailConfig(config: Partial<EnvironmentEmailConfig>): Promise<{ success: boolean; error?: string }> {
    try {
      const currentConfig = await this.getEmailConfig();
      const updatedConfig = {
        ...currentConfig,
        ...config,
        environment: currentEnvironment,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: `email_config_${currentEnvironment}`,
          value: updatedConfig
        }, { onConflict: 'key' });

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error) {
      console.error('Error saving email config:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Initialize email configuration for current environment
   */
  static async initializeEmailConfig(): Promise<{ success: boolean; error?: string }> {
    try {
      const defaultConfig = this.getDefaultConfig();
      return await this.saveEmailConfig(defaultConfig);
    } catch (error) {
      console.error('Error initializing email config:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}
